import {useCallback, useEffect, useState} from "react";

export const useAvatarMedia = ({
  avatarSessionState,
  AvatarSessionState,
  avatarState,
  mediaStream,
  remoteAudioElementRef,
  volume,
  logAvatarDebug,
  logLiveAvatarStream,
}) => {
  const [needsUserUnmute, setNeedsUserUnmute] = useState(false);

  const safePlayVideo = useCallback(async () => {
    const video = mediaStream.current;
    if (!video) return;

    video.setAttribute("playsinline", "true");
    video.setAttribute("webkit-playsinline", "true");
    video.playsInline = true;
    /** Звук только через remoteAudioElement — иначе два аудио-трека + дубль с <audio>. */
    video.muted = true;

    try {
      if (video.paused) {
        await video.play();
      }
      logAvatarDebug("video play success");
      setNeedsUserUnmute(false);
    } catch (error) {
      console.warn("Video autoplay failed:", error);
      try {
        if (video.paused) {
          await video.play();
        }
        logAvatarDebug("video muted play success");
        setNeedsUserUnmute(true);
      } catch (mutedError) {
        console.error("Muted autoplay failed:", mutedError);
      }
    }
  }, [logAvatarDebug, mediaStream]);

  const safePlayAudio = useCallback(async () => {
    const audio = remoteAudioElementRef.current;
    if (!audio) return;

    try {
      await audio.play();
      logAvatarDebug("safePlayAudio success");
      setNeedsUserUnmute(false);
    } catch (_error) {
      logAvatarDebug("safePlayAudio blocked");
      setNeedsUserUnmute(true);
    }
  }, [logAvatarDebug, remoteAudioElementRef]);

  useEffect(() => {
    if (avatarState.stream && mediaStream.current) {
      const video = mediaStream.current;
      const stream = avatarState.stream;
      logLiveAvatarStream?.("video srcObject bound", {
        videoTracks: stream.getVideoTracks().map((t) => ({
          id: t.id,
          muted: t.muted,
          readyState: t.readyState,
          label: t.label,
        })),
        audioTracks: stream.getAudioTracks().map((t) => ({
          id: t.id,
          muted: t.muted,
          readyState: t.readyState,
        })),
      });
      video.muted = true;
      video.srcObject = stream;
      video.onloadedmetadata = () => {
        logLiveAvatarStream?.("video loadedmetadata", {
          videoWidth: video.videoWidth,
          videoHeight: video.videoHeight,
          readyState: video.readyState,
        });
        safePlayVideo();
      };
    }
  }, [avatarState.stream, logLiveAvatarStream, mediaStream, safePlayVideo]);

  useEffect(() => {
    const video = mediaStream?.current;
    if (!video || typeof logLiveAvatarStream !== "function") return;

    const onVideo = (eventName) => () => {
      logLiveAvatarStream(`video element ${eventName}`, {
        readyState: video.readyState,
        networkState: video.networkState,
        paused: video.paused,
        muted: video.muted,
        currentTime: video.currentTime,
      });
    };

    const handlers = [
      ["waiting", onVideo("waiting")],
      ["stalled", onVideo("stalled")],
      ["suspend", onVideo("suspend")],
      ["emptied", onVideo("emptied")],
      ["error", onVideo("error")],
      ["playing", onVideo("playing")],
      ["pause", onVideo("pause")],
    ];

    for (const [evt, fn] of handlers) {
      video.addEventListener(evt, fn);
    }

    return () => {
      for (const [evt, fn] of handlers) {
        video.removeEventListener(evt, fn);
      }
    };
  }, [avatarState.stream, logLiveAvatarStream, mediaStream]);

  useEffect(() => {
    const audio = remoteAudioElementRef?.current;
    if (!audio || typeof logLiveAvatarStream !== "function") return;

    const onAudio = (eventName) => () => {
      logLiveAvatarStream(`remoteAudio element ${eventName}`, {
        readyState: audio.readyState,
        networkState: audio.networkState,
        paused: audio.paused,
        muted: audio.muted,
        currentTime: audio.currentTime,
      });
    };

    const handlers = [
      ["waiting", onAudio("waiting")],
      ["stalled", onAudio("stalled")],
      ["suspend", onAudio("suspend")],
      ["emptied", onAudio("emptied")],
      ["error", onAudio("error")],
      ["playing", onAudio("playing")],
      ["pause", onAudio("pause")],
    ];

    for (const [evt, fn] of handlers) {
      audio.addEventListener(evt, fn);
    }

    return () => {
      for (const [evt, fn] of handlers) {
        audio.removeEventListener(evt, fn);
      }
    };
  }, [avatarState.stream, logLiveAvatarStream, remoteAudioElementRef]);

  useEffect(() => {
    if (mediaStream?.current) {
      mediaStream.current.volume = volume;
    }
    if (remoteAudioElementRef.current) {
      remoteAudioElementRef.current.volume = volume;
    }
  }, [volume, mediaStream, remoteAudioElementRef]);

  useEffect(() => {
    if (!needsUserUnmute || !mediaStream.current) return;

    const unmuteAfterGesture = async () => {
      const video = mediaStream.current;
      if (!video && !remoteAudioElementRef.current) return;

      try {
        if (video) {
          video.muted = true;
          video.volume = volume;
          if (video.paused) {
            await video.play();
          }
        }
        if (remoteAudioElementRef.current) {
          remoteAudioElementRef.current.muted = false;
          remoteAudioElementRef.current.volume = volume;
          await safePlayAudio();
        }
        setNeedsUserUnmute(false);
      } catch (error) {
        console.warn("Unmute after user gesture failed:", error);
      }
    };

    window.addEventListener("touchstart", unmuteAfterGesture, {once: true});
    window.addEventListener("click", unmuteAfterGesture, {once: true});
    window.addEventListener("keydown", unmuteAfterGesture, {once: true});

    return () => {
      window.removeEventListener("touchstart", unmuteAfterGesture);
      window.removeEventListener("click", unmuteAfterGesture);
      window.removeEventListener("keydown", unmuteAfterGesture);
    };
  }, [
    mediaStream,
    needsUserUnmute,
    remoteAudioElementRef,
    safePlayAudio,
    volume,
  ]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (
        document.visibilityState === "visible" &&
        avatarSessionState === AvatarSessionState.CONNECTED
      ) {
        safePlayVideo();
        safePlayAudio();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [AvatarSessionState, avatarSessionState, safePlayAudio, safePlayVideo]);

  return {
    safePlayAudio,
  };
};
